import type { TranslationKey } from "./en.js";

/**
 * The French dictionary. Typed against the English keys, so leaving one out is
 * a compile error rather than an English phrase appearing mid-screen.
 */
export const fr: Record<TranslationKey, string> = {
  // Common
  "common.loading": "Chargement",
  "common.tryAgain": "Réessayer",
  "common.cancel": "Annuler",
  "common.language": "Langue",
  "common.english": "English",
  "common.french": "Français",

  // Errors
  "error.generic": "Une erreur est survenue. Réessayez.",
  "error.crashTitle": "Une erreur est survenue",
  "error.crashBody": "Une erreur inattendue s'est produite.",
  "error.notFoundTitle": "Page introuvable",
  "error.notFoundBody": "Cette page n'existe pas.",
  "error.notFoundHome": "Retour à l'accueil",
  "error.notFoundDetail": "Cette page n'existe pas ou le lien de la partie n'est plus valide.",

  // Auth
  "auth.signIn": "Se connecter",
  "auth.signingIn": "Connexion...",
  "auth.email": "Adresse e-mail",
  "auth.password": "Mot de passe",
  "auth.noAccount": "Pas de compte ?",
  "auth.createOne": "En créer un",
  "auth.continueWith": "ou continuer avec",
  "auth.signInFailed": "Échec de la connexion. Réessayez.",
  "auth.registrationClosed":
    "Ce compte n'est pas enregistré ici, et cette instance HootApp n'accepte pas les inscriptions. Demandez une invitation à l'administrateur.",
  "auth.emailUnverified":
    "Votre fournisseur ne nous a pas transmis d'adresse e-mail vérifiée, nous n'avons donc pas pu créer de compte.",
  "auth.oauthFailed": "La connexion avec ce fournisseur n'a pas abouti. Réessayez.",

  // Join
  "join.nameTaken": "Ce nom est déjà pris. Choisissez-en un autre.",
  "join.notAccepting": "Cette partie n'accepte plus de nouveaux joueurs.",
  "join.suggestFailed": "Impossible de proposer un nom. Saisissez-en un.",
  "join.enterName": "Saisissez votre nom.",
  "join.nameTooLong": "Le nom doit faire 24 caractères au maximum.",
  "join.twoStepIncomplete": "Touchez les trois couleurs affichées sur l'écran de l'animateur.",

  // Player game
  "play.yourScore": "Votre score",
  "play.getReady": "Préparez-vous...",
  "play.getReadyToAnswer": "Préparez-vous à répondre...",
  "play.answerSubmitted": "Réponse envoyée. En attente des résultats...",
  "play.selectAll": "Sélectionnez toutes les bonnes réponses, puis validez.",
  "play.typeAnswer": "Saisissez votre réponse",
  "play.submitAnswer": "Valider la réponse",
  "play.slideContinue": "La suite est sur l'écran de l'animateur.",
  "play.correct": "Correct !",
  "play.incorrect": "Incorrect",
  "play.timesUp": "Temps écoulé",
  "play.slide": "Diapositive",
  "play.noAnswerSubmitted": "Aucune réponse envoyée.",
  "play.nothingToAnswer": "Rien à répondre ici.",
  "play.totalScore": "Score total : {score} pts",
  "play.pointsEarned": "Points gagnés : {points}",
  "play.streak": "{count} d'affilée",
  "play.streakBonus": "(+{bonus} bonus)",
  "play.leaderboard": "Classement",
  "play.yourScoreIs": "Votre score : {score}",
  "play.gameOver": "Partie terminée",
  "play.thanksForPlaying": "Merci d'avoir joué !",
  "play.finalResults": "Résultats finaux",
  "play.finalLeaderboard": "Classement final",
  "play.connectionLost": "Connexion perdue — reconnexion...",
  "play.answerOptions": "Choix de réponse",
  "play.scorePill": "{score} pts",
  "play.questionImage": "Image de la question : {prompt}",

  // Host game
  "hostGame.questionOf": "Question {current} sur {total}",
  "hostGame.questionOfUnknown": "Question {current}",
  "hostGame.answeredCount": "{answered} sur {total} ont répondu",
  "hostGame.secondsLeft": "{seconds} s restantes",
  "hostGame.timerCloses": "Le minuteur ferme les réponses automatiquement",
  "hostGame.timerStarts": "Le minuteur démarre automatiquement",
  "hostGame.spaceToAdvance": "Espace ou Entrée pour continuer",
  "hostGame.continue": "Continuer",
  "hostGame.showLeaderboard": "Afficher le classement",
  "hostGame.nextQuestion": "Question suivante",
  "hostGame.showFinalResults": "Afficher les résultats finaux",
  "hostGame.endSession": "Terminer la partie",
  "hostGame.waitingToStart": "En attente du début de la partie...",
  "hostGame.sessionComplete": "Partie terminée",
  "hostGame.greatGame": "Belle partie !",
  "hostGame.correct": "Correct",
  "hostGame.playersWillType": "Les joueurs saisiront leur réponse.",
  "hostGame.accepted": "Acceptées : {answers}",
  "hostGame.noAnswersSubmitted": "Aucune réponse envoyée.",
  "hostGame.answerCount": " réponses",
  "hostGame.oneAnswer": " réponse",
  "hostGame.answersWithCount": "{count} réponses",
  "hostGame.oneAnswerWithCount": "1 réponse",
  "hostGame.blankAnswer": "(vide)",
  "hostGame.moreAnswersHidden":
    "{count} autres réponses n'ont été données qu'une ou deux fois et ne sont pas affichées.",
  "hostGame.oneMoreAnswerHidden":
    "1 autre réponse n'a été donnée qu'une ou deux fois et n'est pas affichée.",
  "hostGame.didntAnswer": "N'ont pas répondu",
  "hostGame.playersWhoDidntAnswer": "Joueurs qui n'ont pas répondu",
  "hostGame.playersWhoChose": "Joueurs ayant choisi {label}",
  "hostGame.playersWhoTyped": "Joueurs ayant saisi {text}",
  "hostGame.andMore": "+{count} autres",
  "hostGame.answerDistribution": "Répartition des réponses",
  "hostGame.typedAnswerResults": "Réponses saisies",
  "hostGame.morePlayersHidden": "{count} autres joueurs non affichés",
  "hostGame.oneMorePlayerHidden": "1 autre joueur non affiché",
  "hostGame.topThree": "Les trois premiers",
  "hostGame.remainingPlayers": "Autres joueurs",
  "hostGame.finalResults": "Résultats finaux",
  "hostGame.leaderboard": "Classement",
  "hostGame.continueReady": "Continuez lorsque tout le monde est prêt.",
  "hostGame.answeredProgress": "{answered} sur {total} ont répondu",
  "hostGame.phaseQuestion": "Question",
  "hostGame.phaseReveal": "Révélation des réponses",
  "hostGame.phaseLeaderboard": "Classement",
  "hostGame.phasePodium": "Résultats finaux",
  "hostGame.phaseEnded": "Partie terminée",
  "hostGame.phaseAborted": "Partie interrompue",
  "hostGame.firstPlace": "1er",
  "hostGame.secondPlace": "2e",
  "hostGame.thirdPlace": "3e",
  "hostGame.connectionLost": "Connexion perdue — reconnexion...",

  // Quiz shelf
  "quiz.yourQuizzes": "Vos quiz",
  "quiz.shelfIntro": "Créez, modifiez et publiez des quiz pour votre prochaine soirée en famille.",
  "quiz.newQuiz": "Nouveau quiz",
  "quiz.creating": "Création...",
  "quiz.importJson": "Importer du JSON",
  "quiz.noQuizzesTitle": "Aucun quiz pour l'instant",
  "quiz.noQuizzesBody":
    "Votre étagère est vide. Créez un quiz et ajoutez des questions pour préparer une partie.",
  "quiz.createFirst": "Créer votre premier quiz",
  "quiz.loadFailed": "Impossible de charger vos quiz.",
  "quiz.createFailed": "Impossible de créer un nouveau quiz.",
  "quiz.loadingQuizzes": "Chargement des quiz",
  "quiz.questionCount": "{count} questions",
  "quiz.oneQuestion": "1 question",
  "quiz.draft": "brouillon",
  "quiz.published": "publié",
  "quiz.untitled": "Quiz sans titre",

  // Import
  "import.title": "Importer depuis un JSON",
  "import.intro":
    "Collez un fichier de quiz ou choisissez-en un. Il arrive en brouillon, vous pouvez donc le relire avant de le publier.",
  "import.chooseFile": "Choisir un fichier",
  "import.pasteExample": "Coller un exemple",
  "import.jsonLabel": "JSON du quiz",
  "import.importQuiz": "Importer le quiz",
  "import.importing": "Importation...",
  "import.notJson": "Ce n'est pas du JSON valide.",
  "import.readFailed": "Impossible de lire ce fichier.",
  "import.failed": "Impossible d'importer ce quiz.",
  "import.questionLabel": "Question {number}",
  "import.fileLabel": "Fichier",
  "import.panelLabel": "Importer un quiz depuis un JSON",
  "join.enterCode": "Saisissez le code à 6 chiffres",
  "join.codeAriaLabel": "Code de partie à 6 chiffres",
  "join.formLabel": "Saisir le code de la partie",
  "join.codeRequired": "Saisissez le code à 6 chiffres affiché à l'écran.",
  "join.gameNotFound": "Partie introuvable. Vérifiez le code et réessayez.",
  "join.checking": "Vérification...",
  "join.joinGame": "Rejoindre la partie",
  "wait.removedTitle": "Vous avez été retiré de cette partie.",
  "wait.removedBody": "Vous pouvez rejoindre une autre partie avec un nouveau code.",
  "wait.joinedAs": "Vous avez rejoint en tant que",
  "wait.waitingForHost": "En attente du lancement par l'animateur",
  "notFound.joinGame": "Rejoindre une partie",
  "common.loadingEllipsis": "Chargement...",
  "common.secondsLeft": "{seconds} secondes restantes",
  "common.red": "rouge",
  "common.blue": "bleu",
  "common.yellow": "jaune",
  "common.green": "vert",
  "editor.loadingQuiz": "Chargement du quiz",

  // Landing and navigation
  "landing.siteNavigation": "Navigation du site",
  "landing.home": "Accueil de HootApp",
  "landing.kicker": "Quiz en direct",
  "landing.title": "Une soirée quiz, partout.",
  "landing.tagline": "Rejoignez la partie depuis votre téléphone en quelques secondes. Aucune installation, aucune configuration.",
  "landing.joinTitle": "Rejoindre une partie",
  "landing.joinSubtitle": "Saisissez le code affiché sur l'écran de l'animateur.",
  "landing.gameCode": "Code de la partie",
  "landing.join": "Rejoindre",
  "landing.createQuiz": "Créer un quiz",
  "landing.createQuizSubtitle": "Créez et animez un quiz en direct.",
  "landing.signInToCreate": "Se connecter pour créer",
  "landing.howItWorks": "Comment ça marche",
  "landing.how1": "L'animateur partage un code à 6 chiffres.",
  "landing.how2": "Les joueurs rejoignent la partie depuis n'importe quel téléphone.",
  "landing.how3": "Les réponses et les scores se mettent à jour en direct.",
  "landing.footer": "HootApp, le quiz familial privé",

  // Auth forms
  "auth.registrationDisabled": "Les inscriptions sont actuellement désactivées.",
  "auth.enterEmail": "Saisissez votre adresse e-mail.",
  "auth.invalidEmail": "Saisissez une adresse e-mail valide.",
  "auth.displayName": "Nom d'affichage",
  "auth.enterDisplayName": "Saisissez votre nom d'affichage.",
  "auth.passwordMin": "Utilisez au moins 8 caractères pour votre mot de passe.",
  "auth.inviteCode": "Code d'invitation",
  "auth.enterInviteCode": "Saisissez votre code d'invitation.",
  "auth.checkForm": "Vérifiez les informations saisies et réessayez.",
  "auth.registrationFailed": "L'inscription a échoué. Réessayez.",
  "auth.accountExists": "Un compte existe déjà avec cette adresse e-mail. Connectez-vous plutôt.",
  "auth.inviteInvalid": "Ce code d'invitation ne semble pas valide. Vérifiez-le et réessayez.",
  "auth.serverRegistrationFailed": "Nous n'avons pas pu créer votre compte en raison d'un problème serveur. Réessayez.",
  "auth.alreadyAccount": "Vous avez déjà un compte ?",
  "auth.creatingAccount": "Création du compte...",
  "auth.createAccount": "Créer un compte",
  "auth.continueGoogle": "Continuer avec Google",
  "auth.continueDiscord": "Continuer avec Discord",

  // Join and lobby
  "join.chooseName": "Choisissez votre nom",
  "join.yourName": "Votre nom",
  "join.yourNameHere": "Votre nom ici",
  "join.suggestName": "Suggérer",
  "join.suggestingName": "...",
  "join.maximumName": "24 caractères maximum",
  "join.enterPattern": "Saisissez le motif de couleurs affiché sur l'écran de l'animateur.",
  "join.selectedPattern": "Motif en deux étapes sélectionné",
  "join.colorChoices": "Choix de couleurs en deux étapes",
  "join.clearPattern": "Effacer le motif",
  "join.joining": "Connexion...",
  "join.patternRequired": "Saisissez le motif à 3 couleurs affiché sur l'écran de l'animateur.",
  "wait.playerCount": "{count} joueur{suffix} dans la partie",
  "hostLobby.waitingForPlayers": "En attente des joueurs",
  "hostLobby.connected": "Connecté",
  "hostLobby.connecting": "Connexion...",
  "hostLobby.gameCode": "Code de la partie",
  "hostLobby.share": "Partager",
  "hostLobby.copyLink": "Copier le lien de participation",
  "hostLobby.linkCopied": "Lien copié",
  "hostLobby.copyFailed": "Échec de la copie - copiez-le manuellement",
  "hostLobby.qrLabel": "Code QR renvoyant vers {url}",
  "hostLobby.scanToJoin": "Scannez pour rejoindre",
  "hostLobby.players": "Joueurs ({count})",
  "hostLobby.playersInLobby": "Joueurs dans le salon",
  "hostLobby.noPlayers": "Aucun joueur pour l'instant. Partagez le code ci-dessus.",
  "hostLobby.removePlayer": "Retirer {nickname} de la partie",
  "hostLobby.remove": "Retirer",
  "hostLobby.settings": "Paramètres",
  "hostLobby.twoStepJoin": "Participation en deux étapes",
  "hostLobby.pattern": "Motif",
  "hostLobby.randomQuestionOrder": "Ordre aléatoire des questions",
  "hostLobby.randomAnswerOrder": "Ordre aléatoire des réponses",
  "hostLobby.showLeaderboard": "Afficher le classement",
  "hostLobby.controls": "Commandes du salon",
  "hostLobby.unlock": "Déverrouiller le salon",
  "hostLobby.lock": "Verrouiller le salon",
  "hostLobby.start": "Démarrer la partie",

  // Quiz editor
  "quiz.backToQuizzes": "Retour aux quiz",
  "quiz.allQuizzes": "Tous les quiz",
  "quiz.title": "Titre du quiz",
  "quiz.save": "Enregistrer",
  "quiz.saving": "Enregistrement...",
  "quiz.titleSaved": "Titre enregistré.",
  "quiz.host": "Animer",
  "quiz.hosting": "Démarrage...",
  "quiz.hostThisQuiz": "Animer ce quiz",
  "quiz.working": "Traitement...",
  "quiz.unpublish": "Dépublier",
  "quiz.publish": "Publier le quiz",
  "quiz.questions": "Questions",
  "quiz.addQuestion": "Ajouter une question",
  "quiz.noQuestions": "Aucune question pour l'instant",
  "quiz.firstQuestion": "Ajoutez votre première question pour donner vie à ce quiz.",
  "quiz.questionType": "Type de question",
  "quiz.typeSingle": "Réponse unique",
  "quiz.typeMultiple": "Réponses multiples",
  "quiz.typeTrueFalse": "Vrai ou faux",
  "quiz.typeAnswer": "Réponse libre",
  "quiz.typeSlide": "Diapositive",
  "quiz.true": "Vrai",
  "quiz.false": "Faux",
  "quiz.editQuestion": "Modifier la question",
  "quiz.newQuestion": "Nouvelle question",
  "quiz.addQuestionHeading": "Ajouter une question",
  "quiz.slideText": "Texte de la diapositive",
  "quiz.prompt": "Énoncé",
  "quiz.slidePlaceholder": "Que doit afficher cette diapositive ?",
  "quiz.promptPlaceholder": "Que voulez-vous demander ?",
  "quiz.timerSeconds": "Minuteur (secondes)",
  "quiz.image": "Image",
  "quiz.imageForQuestion": "Image de la question {number} : {prompt}",
  "quiz.answerOptions": "Choix de réponse",
  "quiz.chooseEveryCorrect": "Choisissez toutes les bonnes réponses.",
  "quiz.chooseOneCorrect": "Choisissez le bouton radio à côté de la bonne réponse.",
  "quiz.markCorrect": "Marquer la réponse {number} comme correcte",
  "quiz.optionLabel": "Libellé de la réponse {number}",
  "quiz.optionPlaceholder": "Réponse {number}",
  "quiz.removeOption": "Retirer la réponse {number}",
  "quiz.remove": "Retirer",
  "quiz.addOption": "Ajouter une réponse",
  "quiz.acceptedAnswers": "Réponses acceptées",
  "quiz.acceptedAnswersPlaceholder": "Une réponse acceptée par ligne",
  "quiz.saveChanges": "Enregistrer les modifications",
  "quiz.saveQuestion": "Enregistrer la question",
  "quiz.dragReorder": "Faire glisser pour réorganiser",
  "quiz.dragQuestion": "Faire glisser pour réorganiser la question {number}",
  "quiz.moveQuestionUp": "Déplacer la question {number} vers le haut",
  "quiz.moveQuestionDown": "Déplacer la question {number} vers le bas",
  "quiz.editQuestionNumber": "Modifier la question {number}",
  "quiz.deleteQuestionNumber": "Supprimer la question {number}",
  "quiz.deleteQuestionConfirm": "Supprimer la question {number} ?",
  "quiz.correctAnswer": "Bonne réponse : {answer}",
  "quiz.acceptedAnswersSummary": "Réponses acceptées : {answers}",
  "quiz.enterSlideText": "Saisissez le texte de la diapositive.",
  "quiz.enterPrompt": "Saisissez un énoncé de question.",
  "quiz.timerRange": "Le minuteur doit être compris entre {min} et {max} secondes.",
  "quiz.optionNeedsLabel": "Chaque choix de réponse doit avoir un libellé.",
  "quiz.markOneCorrect": "Marquez au moins une réponse comme correcte.",
  "quiz.markExactlyOne": "Marquez exactement une réponse comme correcte.",
  "quiz.addAcceptedAnswer": "Ajoutez au moins une réponse acceptée.",
  "quiz.loadThisFailed": "Impossible de charger ce quiz.",
  "quiz.reorderFailed": "Impossible de réorganiser les questions.",
  "quiz.titleRequired": "Le quiz doit avoir un titre.",
  "quiz.saveTitleFailed": "Impossible d'enregistrer le titre.",
  "quiz.publishStatusFailed": "Impossible de modifier le statut de publication.",
  "quiz.startSessionFailed": "Impossible de démarrer une partie.",
  "quiz.addQuestionFailed": "Impossible d'ajouter la question.",
  "quiz.deleteQuestionFailed": "Impossible de supprimer la question.",
  "quiz.saveQuestionFailed": "Impossible d'enregistrer la question.",

  // Reports and shared admin labels
  "admin.breadcrumb": "Fil d'Ariane",
  "admin.quizzes": "Quiz",
  "admin.edit": "Modifier",
  "admin.reports": "Rapports",
  "admin.detail": "Détails",
  "reports.loading": "Chargement des rapports...",
  "reports.title": "Rapports",
  "reports.empty": "Aucune partie terminée pour l'instant.",
  "reports.sessionReports": "Rapports des parties",
  "reports.quiz": "Quiz",
  "reports.ended": "Terminé",
  "reports.players": "Joueurs",
  "reports.questions": "Questions",
  "reports.status": "Statut",
  "reports.actions": "Actions",
  "reports.view": "Afficher",
  "reports.downloadCsvFor": "Télécharger le CSV de {quiz}",
  "reports.loadingReport": "Chargement du rapport...",
  "reports.notFound": "Rapport introuvable.",
  "reports.endedAt": "Terminé : {date}",
  "reports.downloadCsv": "Télécharger le CSV",
  "reports.downloadCsvExport": "Télécharger l'export CSV",
  "reports.sessionSummary": "Résumé de la partie",
  "reports.participants": "Participants",
  "reports.participantRankings": "Classement des participants",
  "reports.rank": "Rang",
  "reports.nickname": "Pseudonyme",
  "reports.score": "Score",
  "reports.correct": "Bonnes réponses",
  "reports.accuracy": "Précision",
  "reports.questionAnalytics": "Analyse des questions",
  "reports.question": "Question",
  "reports.totalAnswers": "Total des réponses",
  "reports.noPrompt": "(sans énoncé)",
};
